import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';

const getApiBase = () => {
    const apiUrl = import.meta.env.VITE_API_URL;
    return apiUrl.endsWith('/api') ? apiUrl : apiUrl + '/api';
};

const API_BASE = getApiBase();
const authHeader = () => ({ headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } });

import { PAYMENT_MODES } from '../../utils/paymentModes';

const MODE_PAIEMENT_OPTIONS = PAYMENT_MODES;

const FinanceManagement = () => {
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [entities, setEntities] = useState({ vehicules: [], equipes: [] });
    const [showModal, setShowModal] = useState(false);
    
    const [form, setForm] = useState({
        entiteType: 'Vehicule',
        entiteId: '',
        type: 'Carburant',
        categorie: 'Exploitation',
        montant: '',
        datePaiement: new Date().toISOString().split('T')[0],
        modePaiement: 'especes',
        nomRecepteur: '',
        notes: ''
    });

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            // 1. Fetch entities if not already fetched
            let currentVeh = entities.vehicules;
            let currentEq = entities.equipes;
            
            if (currentVeh.length === 0 || currentEq.length === 0) {
                const [resVeh, resEq] = await Promise.all([
                    axios.get(`${API_BASE}/vehicules`, authHeader()),
                    axios.get(`${API_BASE}/equipes-installation`, authHeader())
                ]);
                currentVeh = resVeh.data;
                currentEq = resEq.data;
                setEntities({ vehicules: currentVeh, equipes: currentEq });
            }

            // 2. Fetch all expenses via the global endpoint
            const resDep = await axios.get(`${API_BASE}/depenses`, authHeader());
            
            // 3. Map labels
            const all = resDep.data.map(e => {
                let label = 'Inconnu';
                if (e.entiteType === 'Vehicule') {
                    label = currentVeh.find(v => v._id === e.entiteId)?.matricule || 'Véhicule';
                } else {
                    const eq = currentEq.find(eq => eq._id === e.entiteId);
                    label = eq ? `${eq.chefEquipe?.nom} ${eq.chefEquipe?.prenom}` : 'Équipe';
                }
                return { ...e, entiteLabel: label };
            });

            setExpenses(all);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [entities.vehicules, entities.equipes]);

    useEffect(() => {
        fetchAll();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Filter & Sort States
    const [searchTerm, setSearchTerm] = useState('');
    const [filterEntiteType, setFilterEntiteType] = useState('tous');
    const [filterModePaiement, setFilterModePaiement] = useState('tous');
    const [filterType, setFilterType] = useState('tous');
    const [sortOption, setSortOption] = useState('date_desc');

    // Extract unique types available in loaded expenses
    const availableTypes = React.useMemo(() => {
        const set = new Set();
        expenses.forEach(e => {
            if (e.type?.trim()) set.add(e.type.trim());
        });
        return Array.from(set).sort();
    }, [expenses]);

    const filteredExpenses = React.useMemo(() => {
        return expenses
            .filter(e => {
                const term = searchTerm.trim().toLowerCase();
                if (term) {
                    const type = (e.type || '').toLowerCase();
                    const cat = (e.categorie || '').toLowerCase();
                    const entite = (e.entiteLabel || '').toLowerCase();
                    const notes = (e.notes || '').toLowerCase();
                    const resp = `${e.responsable?.prenom || ''} ${e.responsable?.nom || ''}`.toLowerCase();
                    const recep = (e.nomRecepteur || '').toLowerCase();
                    
                    if (!type.includes(term) && 
                        !cat.includes(term) && 
                        !entite.includes(term) && 
                        !notes.includes(term) && 
                        !resp.includes(term) &&
                        !recep.includes(term)) {
                        return false;
                    }
                }

                if (filterEntiteType !== 'tous' && e.entiteType !== filterEntiteType) return false;
                if (filterModePaiement !== 'tous' && e.modePaiement !== filterModePaiement) return false;
                if (filterType !== 'tous' && e.type !== filterType) return false;

                return true;
            })
            .sort((a, b) => {
                switch (sortOption) {
                    case 'date_desc':
                        return new Date(b.datePaiement) - new Date(a.datePaiement);
                    case 'date_asc':
                        return new Date(a.datePaiement) - new Date(b.datePaiement);
                    case 'montant_desc':
                        return (b.montant || 0) - (a.montant || 0);
                    case 'montant_asc':
                        return (a.montant || 0) - (b.montant || 0);
                    case 'type_asc':
                        return (a.type || '').localeCompare(b.type || '');
                    case 'entite_asc':
                        return (a.entiteLabel || '').localeCompare(b.entiteLabel || '');
                    default:
                        return 0;
                }
            });
    }, [expenses, searchTerm, filterEntiteType, filterModePaiement, filterType, sortOption]);

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            if (form.modePaiement === 'especes' && !(form.nomRecepteur || '').trim()) {
                return Swal.fire('Attention', 'Nom du recepteur obligatoire pour un paiement en espèces', 'warning');
            }
            const endpoint = form.entiteType === 'Vehicule' ? 
                `${API_BASE}/vehicules/${form.entiteId}/expenses` :
                `${API_BASE}/equipes-installation/${form.entiteId}/expenses`;
            
            await axios.post(endpoint, form, authHeader());
            Swal.fire('Succès', 'Dépense enregistrée', 'success');
            setShowModal(false);
            fetchAll();
        } catch (err) {
            Swal.fire('Erreur', 'Erreur lors de la sauvegarde', 'error');
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Supprimer cette dépense ?',
            text: "Cette action est irréversible.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#374151',
            confirmButtonText: 'Oui, supprimer !',
            cancelButtonText: 'Annuler'
        });

        if (result.isConfirmed) {
            try {
                await axios.delete(`${API_BASE}/depenses/${id}`, authHeader());
                Swal.fire('Supprimé !', 'La dépense a été supprimée.', 'success');
                fetchAll();
            } catch (err) {
                Swal.fire('Erreur', 'Impossible de supprimer la dépense.', 'error');
            }
        }
    };

    if (loading) return <LoadingSpinner message="Chargement de la finance..." />;

    const totalMontant = filteredExpenses.reduce((sum, e) => sum + (Number(e.montant) || 0), 0);

    return (
        <div className="fin-container">
            <div className="fin-header">
                <div className="fin-summary">
                    <div className="fin-card">
                        <span className="label">Total Dépenses</span>
                        <span className="value">{totalMontant.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TND</span>
                    </div>
                    <div className="fin-card fin-card-count">
                        <span className="label">Total Dépenses Filtrées</span>
                        <span className="value">{filteredExpenses.length} / {expenses.length}</span>
                    </div>
                </div>
                <button className="btn-add" onClick={() => setShowModal(true)}>+ Nouvelle Dépense</button>
            </div>

            {/* Toolbar Recherche, Filtres & Tri */}
            <div className="fin-toolbar">
                <div className="fin-search-box">
                    <span className="search-icon">🔍</span>
                    <input
                        type="text"
                        placeholder="Rechercher par type, entité, responsable, notes..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button className="search-clear" onClick={() => setSearchTerm('')}>&times;</button>
                    )}
                </div>

                <div className="fin-filter-group">
                    <div className="fin-filter-item">
                        <label>Entité</label>
                        <select value={filterEntiteType} onChange={e => setFilterEntiteType(e.target.value)}>
                            <option value="tous">Toutes les entités</option>
                            <option value="Vehicule">🚗 Véhicules</option>
                            <option value="Equipe">👥 Équipes</option>
                        </select>
                    </div>

                    {availableTypes.length > 0 && (
                        <div className="fin-filter-item">
                            <label>Type de dépense</label>
                            <select value={filterType} onChange={e => setFilterType(e.target.value)}>
                                <option value="tous">Tous les types</option>
                                {availableTypes.map(t => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="fin-filter-item">
                        <label>Mode de paiement</label>
                        <select value={filterModePaiement} onChange={e => setFilterModePaiement(e.target.value)}>
                            <option value="tous">Tous les modes</option>
                            {MODE_PAIEMENT_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="fin-filter-item">
                        <label>Trier par</label>
                        <select value={sortOption} onChange={e => setSortOption(e.target.value)}>
                            <option value="date_desc">Date (Récente → Ancienne)</option>
                            <option value="date_asc">Date (Ancienne → Récente)</option>
                            <option value="montant_desc">Montant (↓ Décroissant)</option>
                            <option value="montant_asc">Montant (↑ Croissant)</option>
                            <option value="type_asc">Type (A → Z)</option>
                            <option value="entite_asc">Entité (A → Z)</option>
                        </select>
                    </div>

                    {(searchTerm || filterEntiteType !== 'tous' || filterModePaiement !== 'tous' || filterType !== 'tous' || sortOption !== 'date_desc') && (
                        <button
                            className="btn-reset-filters"
                            onClick={() => {
                                setSearchTerm('');
                                setFilterEntiteType('tous');
                                setFilterModePaiement('tous');
                                setFilterType('tous');
                                setSortOption('date_desc');
                            }}
                            title="Réinitialiser les filtres"
                        >
                            🔄 Réinitialiser
                        </button>
                    )}
                </div>
            </div>

            <div className="fin-table-wrap">
                <table className="fin-table">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Entité</th>
                            <th>Type</th>
                            <th>Catégorie</th>
                            <th>Montant</th>
                            <th>Responsable</th>
                            <th>Notes</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredExpenses.length === 0 ? (
                            <tr>
                                <td colSpan="8" style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>
                                    <div style={{ fontSize: '1.8rem', marginBottom: '8px', opacity: 0.5 }}>🔍</div>
                                    Aucune dépense ne correspond à vos critères de recherche.
                                </td>
                            </tr>
                        ) : (
                            filteredExpenses.map(e => (
                                <tr key={e._id}>
                                    <td>{new Date(e.datePaiement).toLocaleDateString('fr-FR')}</td>
                                    <td>
                                        <span className={`entite-badge ${e.entiteType.toLowerCase()}`}>
                                            {e.entiteType === 'Vehicule' ? '🚗' : '👥'} {e.entiteLabel}
                                        </span>
                                    </td>
                                    <td><strong>{e.type}</strong></td>
                                    <td>{e.categorie}</td>
                                    <td className="amount">{Number(e.montant || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TND</td>
                                    <td>{e.responsable?.prenom} {e.responsable?.nom}</td>
                                    <td className="notes">{e.notes}</td>
                                    <td>
                                        <button className="btn-delete" onClick={() => handleDelete(e._id)} title="Supprimer">🗑️</button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div className="modal-overlay">
                    <div className="modal-box">
                        <h3>Nouvelle Dépense</h3>
                        <form onSubmit={handleSave}>
                            <div className="form-row">
                                <label>Destinations</label>
                                <select value={form.entiteType} onChange={e => setForm({...form, entiteType: e.target.value, entiteId: ''})}>
                                    <option value="Vehicule">Véhicule</option>
                                    <option value="Equipe">Équipe (Logement/Frais)</option>
                                </select>
                            </div>
                            <div className="form-row">
                                <label>Sélectionner {form.entiteType === 'Vehicule' ? 'le véhicule' : 'l\'équipe'}</label>
                                <select value={form.entiteId} onChange={e => setForm({...form, entiteId: e.target.value})} required>
                                    <option value="">-- Choisir --</option>
                                    {form.entiteType === 'Vehicule' ? 
                                        entities.vehicules.map(v => <option key={v._id} value={v._id}>{v.matricule} ({v.marque})</option>) :
                                        entities.equipes.map(eq => <option key={eq._id} value={eq._id}>{eq.chefEquipe?.nom} {eq.chefEquipe?.prenom}</option>)
                                    }
                                </select>
                            </div>
                            <div className="form-row">
                                <label>Type de dépense</label>
                                <select value={form.type} onChange={e => setForm({...form, type: e.target.value})}>
                                    {form.entiteType === 'Vehicule' ? (
                                        <>
                                            <option value="Carburant">Carburant</option>
                                            <option value="Maintenance">Maintenance</option>
                                            <option value="Assurance">Assurance</option>
                                            <option value="Vignette">Vignette</option>
                                            <option value="Visite technique">Visite technique</option>
                                        </>
                                    ) : (
                                        <>
                                            <option value="Logement">Logement</option>
                                            <option value="Nourriture">Nourriture</option>
                                            <option value="Transport">Transport</option>
                                            <option value="Fournitures">Fournitures</option>
                                        </>
                                    )}
                                    <option value="Autre">Autre</option>
                                </select>
                            </div>
                            <div className="form-row">
                                <label>Montant (TND)</label>
                                <input type="number" step="0.01" value={form.montant} onChange={e => setForm({...form, montant: e.target.value})} required />
                            </div>
                            <div className="form-row">
                                <label>Mode de paiement</label>
                                <select value={form.modePaiement} onChange={e => setForm({...form, modePaiement: e.target.value, nomRecepteur: e.target.value === 'especes' ? form.nomRecepteur : ''})}>
                                    {MODE_PAIEMENT_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                            {form.modePaiement === 'especes' && (
                                <div className="form-row">
                                    <label>Nom du recepteur</label>
                                    <input type="text" value={form.nomRecepteur} onChange={e => setForm({...form, nomRecepteur: e.target.value})} />
                                </div>
                            )}
                            <div className="form-row">
                                <label>Notes</label>
                                <input type="text" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
                            </div>
                            <div className="modal-actions">
                                <button type="button" onClick={() => setShowModal(false)}>Annuler</button>
                                <button type="submit" className="btn-confirm">Enregistrer</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style>{`
                .fin-container { padding: 10px; }
                .fin-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
                .fin-summary { display: flex; gap: 15px; }
                .fin-card { background: #3b82f6; color: white; padding: 15px 25px; border-radius: 12px; box-shadow: 0 4px 12px rgba(59,130,246,0.25); }
                .fin-card-count { background: #1e3a5f; }
                .fin-card .label { font-size: 0.8rem; opacity: 0.9; display: block; }
                .fin-card .value { font-size: 1.4rem; font-weight: 800; }
                
                .btn-add { background: #10b981; color: white; border: none; padding: 12px 20px; border-radius: 8px; font-weight: 600; cursor: pointer; transition: background 0.2s; }
                .btn-add:hover { background: #059669; }

                /* Toolbar Filtres, Recherche & Tri */
                .fin-toolbar {
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 16px 20px;
                    margin-bottom: 20px;
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                    justify-content: space-between;
                    gap: 16px;
                    box-shadow: 0 2px 8px rgba(15, 39, 66, 0.05);
                }

                .fin-search-box {
                    position: relative;
                    flex: 1;
                    min-width: 260px;
                    max-width: 400px;
                    display: flex;
                    align-items: center;
                }

                .fin-search-box .search-icon {
                    position: absolute;
                    left: 12px;
                    font-size: 0.95rem;
                }

                .fin-search-box input {
                    width: 100%;
                    padding: 10px 36px 10px 36px;
                    border: 1.5px solid #cbd5e1;
                    border-radius: 10px;
                    font-size: 0.9rem;
                    color: #1e293b;
                    background: #f8fafc;
                    transition: all 0.2s ease;
                }

                .fin-search-box input:focus {
                    outline: none;
                    border-color: #3b82f6;
                    background: #ffffff;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }

                .fin-search-box .search-clear {
                    position: absolute;
                    right: 10px;
                    background: transparent;
                    border: none;
                    font-size: 1.2rem;
                    color: #94a3b8;
                    cursor: pointer;
                }

                .fin-filter-group {
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                    gap: 14px;
                }

                .fin-filter-item {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .fin-filter-item label {
                    font-size: 0.8rem;
                    font-weight: 700;
                    color: #475569;
                    white-space: nowrap;
                }

                .fin-filter-item select {
                    padding: 8px 12px;
                    border: 1.5px solid #cbd5e1;
                    border-radius: 8px;
                    font-size: 0.85rem;
                    color: #1e293b;
                    background: #ffffff;
                    cursor: pointer;
                    font-weight: 600;
                }

                .fin-filter-item select:focus {
                    outline: none;
                    border-color: #3b82f6;
                }

                .btn-reset-filters {
                    padding: 8px 14px;
                    border: 1px solid #cbd5e1;
                    border-radius: 8px;
                    background: #f1f5f9;
                    color: #475569;
                    font-size: 0.82rem;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .btn-reset-filters:hover {
                    background: #e2e8f0;
                    color: #0f172a;
                }
                
                .fin-table-wrap { background: white; border-radius: 12px; box-shadow: 0 4px 6px rgba(0,0,0,0.05); overflow: hidden; }
                .fin-table { width: 100%; border-collapse: collapse; }
                .fin-table th { background: #f8fafc; padding: 15px; text-align: left; font-size: 0.9rem; color: #64748b; border-bottom: 1px solid #e2e8f0; }
                .fin-table td { padding: 15px; border-bottom: 1px solid #f1f5f9; color: #334155; }
                
                .entite-badge { padding: 4px 8px; border-radius: 6px; font-size: 0.8rem; font-weight: 600; }
                .entite-badge.vehicule { background: #fef3c7; color: #92400e; }
                .entite-badge.equipe { background: #dcfce7; color: #166534; }
                
                .amount { font-weight: 700; color: #1e293b; }
                .notes { font-size: 0.85rem; color: #94a3b8; }
                
                .modal-overlay { position: fixed; top:0; left:0; right:0; bottom:0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
                .modal-box { background: white; padding: 30px; border-radius: 15px; width: 450px; }
                .form-row { margin-bottom: 15px; }
                .form-row label { display: block; margin-bottom: 5px; font-size: 0.9rem; color: #475569; }
                .form-row select, .form-row input { width: 100%; padding: 10px; border: 1px solid #cbd5e1; border-radius: 6px; }
                .modal-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 20px; }
                .btn-confirm { background: #3b82f6; color: white; border: none; padding: 10px 20px; border-radius: 6px; cursor: pointer; }
                
                .btn-delete { 
                    background: #fee2e2; 
                    color: #ef4444; 
                    border: none; 
                    width: 32px; 
                    height: 32px; 
                    border-radius: 6px; 
                    cursor: pointer; 
                    display: flex; align-items: center; justify-content: center;
                    transition: background 0.2s;
                }
                .btn-delete:hover { background: #fecaca; }
            `}</style>
        </div>
    );
};

export default FinanceManagement;
